/**
 * Sticky note and text label resize utilities — shared between CanvasSurface
 * (runtime) and the unit-test suite.
 */

// --- Sticky note constants ---
export const DEFAULT_STICKY_SIZE = 200; // px
export const MIN_STICKY_SIZE     = 100; // 50 % of default
export const MAX_STICKY_SIZE     = 300; // 150 % of default
export const SIZE_STEP           = 50;  // px per keyboard step

/** Padding on each side at DEFAULT_STICKY_SIZE — scales with sticky size on resize. */
export const STICKY_PADDING_AT_DEFAULT = 12;
/** Author row height at DEFAULT_STICKY_SIZE — scales with sticky size on resize. */
export const AUTHOR_LABEL_HEIGHT_AT_DEFAULT = 16;
/** Author name font size at DEFAULT_STICKY_SIZE. */
export const AUTHOR_LABEL_FONT_AT_DEFAULT = 9;
/** Author row top padding at DEFAULT_STICKY_SIZE. */
export const AUTHOR_LABEL_PAD_TOP_AT_DEFAULT = 4;

export interface StickyContentBox {
  /** stickySize / DEFAULT_STICKY_SIZE */
  scale: number;
  padding: number;
  authorH: number;
  authorFont: number;
  authorPadTop: number;
  contentW: number;
  contentH: number;
}

/**
 * Content geometry for a square sticky. Padding and author chrome scale with size
 * so the content aspect ratio stays constant — resize is a uniform scale (no wrap
 * reflow / vertical clipping from fixed chrome).
 */
export function stickyContentBox(stickySize: number): StickyContentBox {
  const scale = stickySize / DEFAULT_STICKY_SIZE;
  const padding = STICKY_PADDING_AT_DEFAULT * scale;
  const authorH = AUTHOR_LABEL_HEIGHT_AT_DEFAULT * scale;
  return {
    scale,
    padding,
    authorH,
    authorFont: AUTHOR_LABEL_FONT_AT_DEFAULT * scale,
    authorPadTop: AUTHOR_LABEL_PAD_TOP_AT_DEFAULT * scale,
    contentW: stickySize - padding * 2,
    contentH: stickySize - padding * 2 - authorH,
  };
}

/** Canonical content box used for fit — all clients scale from this geometry. */
export const DEFAULT_STICKY_CONTENT_BOX = stickyContentBox(DEFAULT_STICKY_SIZE);

// --- Text label constants (font-size based) ---
export const DEFAULT_LABEL_FONT  = 48;  // px — headline-sized, comparable to a default postit
export const MIN_LABEL_FONT      = 24;  // 50 % of default
export const MAX_LABEL_FONT      = 72;  // 150 % of default
export const LABEL_FONT_STEP     = 12;  // px per keyboard step

export type ResizeCorner = 'tl' | 'tr' | 'br' | 'bl';

export interface ResizeDragState {
  corner: ResizeCorner;
  startClientX: number;
  startClientY: number;
  startStickyX: number;
  startStickyY: number;
  startSize: number;
  /** Canvas scale factor at the moment the drag started. */
  scale: number;
  stickyId: string;
  /**
   * Measured width (clientWidth) of the element at drag start.
   * Used by text label resize to keep the anchor corner fixed despite fit-content width.
   */
  startElemWidth?: number;
  /**
   * Measured height (clientHeight) of the element at drag start.
   */
  startElemHeight?: number;
}

/**
 * Projects a board-space pointer delta onto the grow axis for the dragged corner.
 * Shared by sticky and label resize so both track the pointer the same way.
 *
 * `aspectRatio` is height / width (1 for square stickies).
 */
function projectCornerDelta(
  corner: ResizeCorner,
  dx: number,
  dy: number,
  aspectRatio: number,
): number {
  if (corner === 'br') return  (dx + dy * aspectRatio) / (1 + aspectRatio);
  if (corner === 'tl') return -(dx + dy * aspectRatio) / (1 + aspectRatio);
  if (corner === 'tr') return  (dx - dy * aspectRatio) / (1 + aspectRatio);
  return (-dx + dy * aspectRatio) / (1 + aspectRatio); // bl
}

/**
 * Computes the new element position and size from a resize drag.
 *
 * The "opposite" corner from the one being dragged is used as the fixed
 * anchor. The drag delta is projected onto the diagonal axis of that corner.
 *
 * `aspectRatio` (height / width) controls the projection and y-anchor adjustment.
 * Pass 1 for square stickies (default).
 *
 * newSize is always rounded to the nearest integer and clamped to
 * [minSize, maxSize].
 */
export function computeResize(
  rs: ResizeDragState,
  clientX: number,
  clientY: number,
  minSize = MIN_STICKY_SIZE,
  maxSize = MAX_STICKY_SIZE,
  aspectRatio = 1,
): { newX: number; newY: number; newSize: number } {
  const dx = (clientX - rs.startClientX) / rs.scale;
  const dy = (clientY - rs.startClientY) / rs.scale;
  const proj = projectCornerDelta(rs.corner, dx, dy, aspectRatio);

  const newSize = Math.round(
    Math.min(maxSize, Math.max(minSize, rs.startSize + proj)),
  );

  // The anchor (opposite) corner stays fixed; adjust x/y so it doesn't move.
  // For non-square elements the y adjustment scales by the aspect ratio.
  let newX = rs.startStickyX;
  let newY = rs.startStickyY;
  if (rs.corner === 'tl' || rs.corner === 'bl') newX = rs.startStickyX + rs.startSize - newSize;
  if (rs.corner === 'tl' || rs.corner === 'tr') newY = rs.startStickyY + (rs.startSize - newSize) * aspectRatio;

  return { newX, newY, newSize };
}

/**
 * Computes the new font size and position for a text label corner-drag.
 *
 * Labels are width: fit-content — size is driven by font, not a stored box. To
 * match sticky/postit pointer tracking, resize in measured visual-width space
 * via computeResize, then convert the resulting width back to a font size:
 *   newFont ≈ startFont × (newWidth / startElemWidth)
 *
 * When startElemWidth is missing, falls back to treating font size as the
 * resize "size" (same projection as a sticky) and leaves x/y unchanged.
 */
export function computeLabelFontResize(
  rs: ResizeDragState,
  clientX: number,
  clientY: number,
): { newFont: number; newX: number; newY: number } {
  const sw = rs.startElemWidth  ?? 0;
  const sh = rs.startElemHeight ?? 0;
  const startFont = rs.startSize;

  // No measured box — treat font px like sticky size px for the font change only;
  // without element dimensions we cannot anchor-correct, so keep position fixed.
  if (sw <= 0 || startFont <= 0) {
    const { newSize } = computeResize(
      rs, clientX, clientY, MIN_LABEL_FONT, MAX_LABEL_FONT, 1,
    );
    return { newFont: newSize, newX: rs.startStickyX, newY: rs.startStickyY };
  }

  // Same path as postits: project drag onto visual width, clamp in width space
  // to the widths that correspond to [MIN_LABEL_FONT, MAX_LABEL_FONT].
  const aspectRatio = sh > 0 ? sh / sw : 1;
  const minWidth = sw * (MIN_LABEL_FONT / startFont);
  const maxWidth = sw * (MAX_LABEL_FONT / startFont);
  const sizeRs: ResizeDragState = { ...rs, startSize: sw };
  const { newSize } = computeResize(
    sizeRs, clientX, clientY, minWidth, maxWidth, aspectRatio,
  );

  const newFont = Math.min(
    MAX_LABEL_FONT,
    Math.max(MIN_LABEL_FONT, Math.round(startFont * (newSize / sw))),
  );

  // Anchor from the font we actually apply (fit-content width follows font).
  const ratio = newFont / startFont;
  let newX = rs.startStickyX;
  let newY = rs.startStickyY;
  if (rs.corner === 'tl' || rs.corner === 'bl') newX = rs.startStickyX + sw * (1 - ratio);
  if (rs.corner === 'tl' || rs.corner === 'tr') newY = rs.startStickyY + sh * (1 - ratio);

  return { newFont, newX, newY };
}
